import { InsightsNavbar } from "@/components/InsightsNavbar";
import { AnimatedSection } from "@/components/AnimatedSection";
import { ScrollProgressIndicator } from "@/components/ScrollProgressIndicator";
import { Footer } from "@/components/Footer";
import { InsightsLeadModal } from "@/components/InsightsLeadModal";
import { InsightsBreadcrumb } from "@/components/InsightsBreadcrumb";
import { InsightSEO } from "@/hooks/useInsightSEO";
import { RelatedTools } from "@/components/RelatedTools";
import { InsightEngagementTracker } from "@/components/InsightEngagementTracker";
import { InsightFAQSection } from "@/components/InsightFAQSection";
import { InsightBenchmarks } from "@/components/InsightBenchmarks";
import { TableOfContents } from "@/components/TableOfContents";
import { Badge } from "@/components/Badge";
import { StatPill } from "@/components/StatPill";
import {
  TrendingDown,
  AlertTriangle,
  Bot,
  Building2,
  Lightbulb,
  ArrowRight,
  Users,
  DollarSign,
  LineChart,
  Sparkles,
} from "lucide-react";
import amanImage from "@/assets/aman-sajid.png";

const HeroSection = () => (
  <section className="pt-8 sm:pt-12">
    <Badge>
      <TrendingDown className="h-3 w-3 mr-1" />
      Freelance Market
    </Badge>

    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mt-4 mb-3 leading-tight">
      The Upwork, Fiverr & Freelancer.com Collapse: Inside the Numbers
    </h1>

    <p className="text-lg sm:text-xl text-muted-foreground mb-6 max-w-3xl">
      The three publicly listed freelance marketplaces — Upwork, Fiverr and
      Freelancer.com (parent: ASX-listed Freelancer Limited) — are all
      contracting at the same time, and the public markets are pricing in a
      much smaller future. Here is what the filings, earnings calls and stock
      charts actually say.
    </p>

    <div className="flex flex-wrap gap-3 mb-8">
      <StatPill value="-47K" label="Upwork active clients YoY" />
      <StatPill value="-56%" label="UPWK stock YTD 2026" />
      <StatPill value="-13.6%" label="Fiverr active buyers YoY" />
      <StatPill value="-7.1%" label="Freelancer.com group GMV YoY" />
      <StatPill value="-37%" label="FLN.AX stock YTD 2026" />
    </div>

    <div className="bg-muted/30 border border-border rounded-xl p-6 sm:p-8">
      <h2
        id="overview"
        className="text-xl sm:text-2xl font-semibold text-foreground mb-4 flex items-center gap-2"
      >
        <LineChart className="h-5 w-5 text-destructive" />
        Three public marketplaces, the same downward chart
      </h2>
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">Upwork (NASDAQ: UPWK)</strong>{" "}
            ended 2025 with roughly 785,000 active clients, down from about
            832,000 a year earlier — a ~47,000 client loss in 12 months.
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">$787.8M</span> full-year 2025 revenue (+2% YoY)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">-19%</span> single-day stock drop after Q4 2025 earnings (Feb 10, 2026)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">~$8</span> share price (May 2026) vs $60.70 peak in 2021
            </li>
          </ul>
        </div>
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">Fiverr (NYSE: FVRR)</strong> is
            being repriced just as aggressively after a weak Q4 2025 and a
            major miss on 2026 guidance.
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">-13.6%</span> active buyers YoY in Q4 2025
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">$380–420M</span> 2026 revenue guide vs ~$456.8M consensus
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">-13% to -17%</span> stock drop post-earnings (Feb 18, 2026)
            </li>
          </ul>
        </div>
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">Freelancer Limited (ASX: FLN)</strong>{" "}
            — parent of Freelancer.com and Escrow.com — reported a -7.1%
            decline in group GMV for FY25, with the core Freelancer segment
            essentially flat.
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">A$881.5M</span> group GMV (-7.1% YoY)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">+0.7%</span> Freelancer.com segment revenue growth (FY25)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-semibold">~-37%</span> FLN.AX stock YTD 2026
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const Takeaway = () => (
  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 sm:p-8">
    <div className="flex items-start gap-4">
      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
        <Lightbulb className="h-6 w-6 text-primary" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Key Takeaway</h3>
        <p className="text-muted-foreground mb-4">
          The freelance marketplace model that won the 2010s is being
          structurally compressed by generative AI. The losses at Upwork,
          Fiverr and Freelancer.com are not a one-quarter blip — they are
          showing up across all three public marketplaces in active clients,
          GMV, revenue growth, guidance, layoffs, and stock prices at the
          same time.
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Commodity gigs are going to AI, not freelancers</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Buyers prefer vetted agencies for real projects</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Public markets are pricing in a smaller TAM</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FounderQuote = () => (
  <div className="bg-card border border-border rounded-xl p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row gap-6 items-start">
      <img
        src={amanImage}
        alt="Aman Sajid"
        className="w-16 h-16 rounded-full object-cover border-2 border-primary/20 shrink-0"
      />
      <div>
        <blockquote className="text-lg sm:text-xl text-foreground italic mb-4">
          "Upwork lost forty-seven thousand active clients in a single year
          and its stock is down more than fifty percent year to date. Fiverr's
          active buyers fell almost fourteen percent. And now Freelancer.com's
          parent is reporting a seven percent drop in group GMV with its stock
          off about thirty-seven percent year to date. Three publicly listed
          marketplaces, three versions of the same chart — this isn't a vibe
          shift, it's the public markets telling us the freelance marketplace
          model is being eaten by generative AI from the bottom up."
        </blockquote>

        <div className="bg-muted/40 border border-border rounded-lg p-4 mb-4">
          <p className="text-sm font-semibold text-foreground mb-3">
            Aman's read on the numbers:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">All three public marketplaces are contracting in the same quarter.</strong>{" "}
                When UPWK, FVRR and FLN.AX all miss in the same window, that's a category problem, not a company problem.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">The bottom of the market is gone, not just slow.</strong>{" "}
                Blog posts, basic logos, translations and simple code are being completed inside ChatGPT, Claude and Midjourney — they never reach a marketplace at all.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Serious buyers are choosing vetted partners, not bid auctions.</strong>{" "}
                For real projects, businesses want one accountable team — an agency or fractional specialist — instead of sifting through 40 proposals on a marketplace.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">'AI-first' pivots are a defense, not a growth story.</strong>{" "}
                Fiverr's ~30% layoff and Upwork's cost cuts are about protecting margin in a shrinking TAM, not capturing a new one.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">What we tell IT Dukes clients:</strong>{" "}
                use AI tools directly for commodity work, and hire a small accountable team (in-house or agency) for anything that touches your roadmap, your data, or your customers.
              </span>
            </li>
          </ul>
        </div>

        <div>
          <p className="font-semibold text-foreground">Aman Sajid</p>
          <p className="text-sm text-muted-foreground">Founder, IT Dukes</p>
        </div>
      </div>
    </div>
  </div>
);

const headlineNumbers = [
  {
    icon: Users,
    value: "~47,000",
    label: "Upwork active clients lost in 2025",
    sub: "832K (2024) → 785K (2025), the largest YoY decline on record.",
    color: "text-destructive",
  },
  {
    icon: TrendingDown,
    value: "-56%",
    label: "UPWK stock, YTD 2026",
    sub: "Down ~87% from its $60.70 peak in July 2021 (price ~$8, May 2026).",
    color: "text-amber-500",
  },
  {
    icon: DollarSign,
    value: "$787.8M",
    label: "Upwork 2025 revenue (+2% YoY)",
    sub: "Growth has slowed from double digits to near flat.",
    color: "text-blue-500",
  },
  {
    icon: Users,
    value: "-13.6%",
    label: "Fiverr active buyers YoY (Q4 2025)",
    sub: "Buyer base shrinking even as revenue grows modestly.",
    color: "text-destructive",
  },
  {
    icon: AlertTriangle,
    value: "$380–420M",
    label: "Fiverr 2026 revenue guide",
    sub: "vs ~$456.8M consensus — a ~12% midpoint miss; stock dropped 13–17% on Feb 18, 2026.",
    color: "text-orange-500",
  },
  {
    icon: Building2,
    value: "~250",
    label: "Fiverr employees laid off",
    sub: "~30% of workforce, Sep 15, 2025 — pivot to 'AI-first' company.",
    color: "text-purple-500",
  },
  {
    icon: TrendingDown,
    value: "-7.1%",
    label: "Freelancer Ltd group GMV YoY (FY25)",
    sub: "Escrow.com -8.6%, Freelancer.com segment GMV +2.3% — a low-quality 'growth'.",
    color: "text-destructive",
  },
  {
    icon: LineChart,
    value: "~-37%",
    label: "FLN.AX stock YTD 2026",
    sub: "Share price A$0.145 (late Apr 2026), down -14.7% in 5 days after Q1 2026 update.",
    color: "text-amber-500",
  },
  {
    icon: DollarSign,
    value: "A$40.9M",
    label: "Freelancer.com FY25 segment revenue (+0.7%)",
    sub: "Essentially flat — group revenue growth came almost entirely from Escrow.com (+18.8%).",
    color: "text-blue-500",
  },
];

const HeadlineNumbers = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="headline-numbers"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2"
    >
      <LineChart className="h-6 w-6 text-destructive" />
      Headline numbers
    </h2>
    <p className="text-muted-foreground mb-8 max-w-3xl">
      Pulled directly from Upwork and Fiverr SEC filings, Freelancer Limited's
      ASX FY25 Business Update (25 Feb 2026), Q4 2025 / Q1 2026 earnings,
      and market data from Yahoo Finance, TradingView, Macrotrends and
      MarketScreener.
    </p>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      {headlineNumbers.map((n) => (
        <div
          key={n.label}
          className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors"
        >
          <div className={`p-2 rounded-lg bg-muted w-fit mb-3 ${n.color}`}>
            <n.icon className="h-5 w-5" />
          </div>
          <div className={`text-2xl font-bold mb-1 ${n.color}`}>{n.value}</div>
          <div className="font-semibold text-foreground mb-1">{n.label}</div>
          <p className="text-sm text-muted-foreground">{n.sub}</p>
        </div>
      ))}
    </div>
  </section>
);

const drivers = [
  {
    icon: Bot,
    title: "Generative AI is eating commodity gigs",
    body:
      "Wharton research (Prof. Manav Raj, 2024) measured a clear drop in freelance demand for writing, translation and basic coding after ChatGPT's late-2022 launch. The same gigs that filled Upwork, Fiverr and Freelancer.com are now being handled directly by ChatGPT, Claude and Midjourney.",
  },
  {
    icon: Sparkles,
    title: "Buyers are going direct to AI tools",
    body:
      "A blog post, a simple logo, a translation, a basic landing page — most buyers no longer post a gig for these. They open ChatGPT or Midjourney and finish it themselves. Coverage from Purposed ('AI Is Eating Upwork From the Bottom Up', 2026) documents this pattern across categories.",
  },
  {
    icon: Building2,
    title: "Real projects are leaving open marketplaces",
    body:
      "For higher-value work, buyers increasingly prefer vetted agencies, fractional specialists, or small in-house teams over open marketplaces. They want accountability, IP protection and quality control that race-to-the-bottom bidding on Upwork, Fiverr or Freelancer.com doesn't provide.",
  },
  {
    icon: AlertTriangle,
    title: "Platforms are pivoting to 'AI-first' faster than supply can adapt",
    body:
      "Fiverr's 'AI-first' company pivot and ~250 layoffs in September 2025 (~30% of staff) are the loudest example, but Upwork and Freelancer.com are also leaning into AI tooling and trimming costs. Freelancers across all three platforms worry these moves accelerate their own replacement.",
  },
  {
    icon: LineChart,
    title: "Public markets are pricing in a smaller TAM",
    body:
      "UPWK is down ~56% YTD 2026 (~87% from its 2021 peak), FVRR cut 2026 revenue guidance ~12% below consensus, and FLN.AX is down ~37% YTD. Three independent investor bases are reaching the same conclusion at the same time — freelance marketplaces are a structurally smaller market going forward.",
  },
];

const WhyDeclining = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="why-declining"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2"
    >
      <Bot className="h-6 w-6 text-primary" />
      Why the decline is structural, not cyclical
    </h2>
    <p className="text-muted-foreground mb-8 max-w-3xl">
      All three companies are still profitable, but the same forces are
      compressing the marketplace model across Upwork, Fiverr and
      Freelancer.com at the same time.
    </p>

    <div className="grid gap-4 sm:gap-6">
      {drivers.map((d) => (
        <div
          key={d.title}
          className="bg-card border border-border rounded-xl p-5 sm:p-6 hover:border-primary/30 transition-colors"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0">
              <d.icon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {d.title}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">{d.body}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Sources = () => (
  <section className="py-8 sm:py-10">
    <h2
      id="sources"
      className="text-xl sm:text-2xl font-bold text-foreground mb-4"
    >
      Sources
    </h2>
    <ul className="list-disc list-inside text-sm sm:text-base text-muted-foreground space-y-2">
      <li>Upwork Inc. — Q4 2025 shareholder letter & 10-K filings (SEC)</li>
      <li>Fiverr International Ltd. — Q4 2025 earnings release & 20-F (SEC)</li>
      <li>Freelancer Limited — FY25 Business Update & Appendix 4E (ASX, 25 Feb 2026)</li>
      <li>Freelancer Limited — Q1 2026 earnings update (22 Apr 2026)</li>
      <li>Yahoo Finance, TradingView, Macrotrends — UPWK & FVRR price history</li>
      <li>MarketScreener — FLN.AX price history & 5-day moves</li>
      <li>AInvest, StockStory, TIKR — earnings analysis (Feb 2026)</li>
      <li>TechCrunch & Freelance Informer — Fiverr layoffs and 'AI-first' coverage</li>
      <li>Wharton Knowledge — Manav Raj research on AI displacement of freelance work</li>
      <li>Purposed (Substack) — "AI Is Eating Upwork From the Bottom Up" (May 2026)</li>
      <li>Toptal Talent Market Report Q4 2025; AgencyReview Toptal Review 2026</li>
    </ul>
  </section>
);

const UpworkFiverrDecline = () => {
  return (
    <>
      <InsightSEO
        slug="upwork-fiverr-decline"
        additionalKeywords={[
          "Upwork decline",
          "Fiverr decline",
          "Freelancer.com decline",
          "FLN ASX stock",
          "Freelancer Limited FY25",
          "freelance market 2026",
          "UPWK stock",
          "FVRR stock",
          "AI replacing freelancers",
          "generative AI freelance",
          "Fiverr layoffs",
        ]}
      />
      <InsightEngagementTracker
        slug="upwork-fiverr-decline"
        title="Upwork, Fiverr & Freelancer.com Decline"
      />
      <InsightsNavbar showBack />
      <ScrollProgressIndicator />
      <TableOfContents />
      <InsightsLeadModal />
      <main className="page-container pb-16 sm:pb-20">
        <InsightsBreadcrumb
          category="Business IT"
          currentPage="Upwork, Fiverr & Freelancer.com Decline"
        />
        <HeroSection />

        <AnimatedSection delay={100} className="mt-10 sm:mt-12">
          <Takeaway />
        </AnimatedSection>

        <AnimatedSection delay={50} className="mt-8 sm:mt-10">
          <FounderQuote />
        </AnimatedSection>

        <div className="section-divider" />

        <AnimatedSection delay={0}>
          <HeadlineNumbers />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <WhyDeclining />
        </AnimatedSection>

        {/* Benchmark Tables */}
        <AnimatedSection delay={0}>
          <InsightBenchmarks insightSlug="upwork-fiverr-decline" />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <Sources />
        </AnimatedSection>

        {/* FAQ Section */}
        <AnimatedSection delay={0}>
          <InsightFAQSection insightSlug="upwork-fiverr-decline" />
        </AnimatedSection>

        {/* Related Tools Section */}
        <AnimatedSection delay={0}>
          <RelatedTools insightSlug="upwork-fiverr-decline" />
        </AnimatedSection>
      </main>
      <Footer />
    </>
  );
};

export default UpworkFiverrDecline;